import {
  Typography,
  Box,
  Stack,
  Tabs,
  Tab,
  Paper,
  Container,
} from "@mui/material";
import { Outlet, Link, useLocation, useOutletContext } from "react-router-dom";
import { useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import SuperAdminService from "../../services/SuperAdminService";
import type { SuperAdminOrganization } from "../../services/SuperAdminService";
import LoadingSpinner from "../../components/LoadingSpinner";
import { AdminPanelSettings, People, Security } from "@mui/icons-material";

export interface SuperAdminLayoutContextValue {
  organizations: SuperAdminOrganization[];
  selectedOrganizationId?: string;
  selectedOrganizationLabel?: string;
  isLoadingOrganizations: boolean;
  onSelectOrganization: (organizationId: string) => void;
  refetchOrganizations: () => Promise<unknown>;
}

export const useSuperAdminOutletContext = () =>
  useOutletContext<SuperAdminLayoutContextValue>();

const SuperAdminLayout = () => {
  const location = useLocation();

  const {
    data: organizations = [],
    isLoading,
    refetch,
  } = useQuery({
    queryKey: ["super-admin", "organizations"],
    queryFn: SuperAdminService.fetchOrganizations,
  });

  const [selectedOrganizationId, setSelectedOrganizationId] = useState<
    string | undefined
  >();

  useEffect(() => {
    if (!isLoading && organizations.length > 0 && !selectedOrganizationId) {
      setSelectedOrganizationId(organizations[0].id);
    }
  }, [isLoading, organizations, selectedOrganizationId]);

  const selectedOrganizationLabel = useMemo(() => {
    if (!selectedOrganizationId) return "";
    return (
      organizations.find((org) => org.id === selectedOrganizationId)?.name || ""
    );
  }, [organizations, selectedOrganizationId]);

  // Determine current tab based on path
  const currentTab = location.pathname.includes("/super-admin/users") ? 1 : 0;

  if (isLoading && organizations.length === 0) {
    return <LoadingSpinner message="Chargement des organisations..." />;
  }

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      <Stack spacing={3}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <AdminPanelSettings color="primary" sx={{ fontSize: 40 }} />
          <Box>
            <Typography variant="h4" fontWeight="bold" color="text.primary">
              Administration
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Gestion des rôles et des utilisateurs
            </Typography>
          </Box>
        </Box>

        <Paper sx={{ borderRadius: 2 }}>
          <Tabs
            value={currentTab}
            variant="scrollable"
            scrollButtons="auto"
            sx={{
              borderBottom: 1,
              borderColor: "divider",
              "& .MuiTab-root": { minHeight: 64, fontWeight: 600 },
            }}
          >
            <Tab
              label="Rôles"
              icon={<Security />}
              iconPosition="start"
              component={Link}
              to="/super-admin/roles"
            />
            <Tab
              label="Utilisateurs"
              icon={<People />}
              iconPosition="start"
              component={Link}
              to="/super-admin/users"
            />
          </Tabs>
        </Paper>

        <Outlet
          context={{
            organizations,
            selectedOrganizationId,
            selectedOrganizationLabel,
            isLoadingOrganizations: isLoading,
            onSelectOrganization: setSelectedOrganizationId,
            refetchOrganizations: refetch,
          }}
        />
      </Stack>
    </Container>
  );
};

export default SuperAdminLayout;
